/* eslint-disable no-unreachable */
/* eslint-disable no-use-before-define */
/* eslint-disable no-unused-vars */
/* eslint-disable no-restricted-syntax */
/* eslint-disable no-shadow */
import { message } from 'antd';
import { localeKeyToString } from 'pages/utils';
import * as downloadService from 'services/transfer';

/**
 * 다운로드 매니저 생성 함수
 * 파일과 폴더 다운로드 처리 및 상태 관리
 */
function createDownloadManager() {
  // ========== 상태 관리 변수 ==========
  let stateUpdater = null;
  let localeResource = null;
  const activeDownloads = {};
  const pollingTimers = {};
  let isDownloading = false;
  let autoCloseTimer = null;

  // 다운로드 상태
  const DOWNLOAD_STATUS = {
    PREPARING: 'preparing',
    COMPRESSING: 'compressing',
    READY: 'ready',
    DOWNLOADING: 'downloading',
    COMPLETED: 'completed',
    ERROR: 'error',
    CANCELLED: 'cancelled',
    FAILED: 'failed',
  };

  // ========== 내부 유틸리티 함수 ==========

  /**
   * 다운로드 상태에 따른 메시지 반환
   * @param {string} status 다운로드 상태
   * @returns {string} 상태 메시지
   */
  const getStatusMessage = (status) => {
    if (!localeResource) return status;

    const messageKeys = {
      [DOWNLOAD_STATUS.COMPRESSING]: 'txt.file-transfer.status-compressing',
      [DOWNLOAD_STATUS.READY]: 'txt.file-transfer.status-ready',
      [DOWNLOAD_STATUS.FAILED]: 'txt.file-transfer.status-failed',
    };

    const key = messageKeys[status];
    return key ? localeResource[key] || status : status;
  };

  /**
   * API 응답에서 오류 메시지 추출
   * @param {Object} response API 응답 객체
   * @param {string} defaultKey 기본 에러 메시지 키
   * @returns {string} 국제화 에러 메시지
   */
  const getErrorMessage = (error, defaultKey) => {
    if (!localeResource) return 'Error occurred';

    const messageKey = error?.message || defaultKey;
    return localeResource[messageKey] || localeResource[defaultKey] || 'Error occurred';
  };

  /**
   * 다운로드 항목 ID 생성
   * @returns {string} 고유 다운로드 ID
   */
  const generateDownloadId = () => `download_${Date.now()}`;

  /**
   * 다운로드 항목의 표시 이름 생성
   * @param {Array} items 다운로드 항목 배열
   * @returns {string} 표시할 이름
   */
  const createDisplayName = (items) => {
    if (!items || items.length === 0) return '';

    if (items.length === 1) {
      return items[0].name;
    }
    return localeKeyToString({
      key: 'txt.file-transfer.compressing-files',
      values: { 0: items.length },
    });
  };

  /**
   * 항목 총 크기 계산
   * @param {Array} items 다운로드 항목 배열
   * @returns {number} 총 크기 (바이트)
   */
  const calculateTotalSize = (items) => {
    if (!items || items.length === 0) return 0;
    return items.reduce((sum, item) => sum + (item.size || 0), 0);
  };

  /**
   * 모든 다운로드가 완료되었는지 확인
   * @returns {boolean} 완료 여부
   */
  const areAllDownloadsComplete = () => {
    if (!stateUpdater || !stateUpdater.getItems) return true;

    const items = stateUpdater.getItems();
    if (!items || items.length === 0) return true;

    for (const item of items) {
      const status = stateUpdater.getStatus(item.uid);
      if (
        status === DOWNLOAD_STATUS.PREPARING ||
        status === DOWNLOAD_STATUS.COMPRESSING ||
        status === DOWNLOAD_STATUS.DOWNLOADING
      ) {
        return false;
      }
    }

    return true;
  };

  /**
   * 자동 닫기 타이머 설정
   */
  const setAutoCloseTimer = () => {
    // 이미 타이머가 있다면 제거
    if (autoCloseTimer) {
      clearTimeout(autoCloseTimer);
      autoCloseTimer = null;
    }

    // 모든 다운로드가 완료되었는지 확인
    if (areAllDownloadsComplete() && stateUpdater) {
      // 5초 후에 패널 닫기
      autoCloseTimer = setTimeout(() => {
        if (stateUpdater.clearCompleted) {
          stateUpdater.clearCompleted();
        }
      }, 5000);
    }
  };

  // ========== 상태 업데이트 함수 ==========

  /**
   * 다운로드 항목 상태 업데이트
   * @param {string} itemUid 항목 ID
   * @param {string} status 상태
   * @param {string} message 메시지 (선택적)
   */
  const updateItemStatus = (itemUid, status, message) => {
    if (!stateUpdater) return;

    stateUpdater.updateStatus(itemUid, status);

    if (message) {
      stateUpdater.updateMessage(itemUid, message);
    }

    // 특정 상태에 따른 메시지 자동 설정
    if (!message) {
      let statusMessage = '';

      if (status === DOWNLOAD_STATUS.DOWNLOADING) {
        const downloadItem = activeDownloads[itemUid];
        if (downloadItem && downloadItem.items.length === 1) {
          statusMessage = localeResource['txt.file-transfer.downloading'];
        }
      } else if (status === DOWNLOAD_STATUS.COMPRESSING) {
        statusMessage = localeResource['txt.file-transfer.compressing'];
      } else if (status === DOWNLOAD_STATUS.READY) {
        statusMessage = localeResource['txt.file-transfer.download-ready'];
      }

      if (statusMessage) {
        stateUpdater.updateMessage(itemUid, statusMessage);
      }
    }

    // 상태가 변경되면 자동 닫기 확인
    if (
      status === DOWNLOAD_STATUS.COMPLETED ||
      status === DOWNLOAD_STATUS.ERROR ||
      status === DOWNLOAD_STATUS.CANCELLED
    ) {
      setAutoCloseTimer();
    }
  };

  /**
   * 다운로드 진행률 업데이트
   * @param {string} itemUid 항목 ID
   * @param {number} progress 진행률 (0-100)
   */
  const updateProgress = (itemUid, progress) => {
    if (!stateUpdater || !stateUpdater.updateProgress) return;

    try {
      stateUpdater.updateProgress(itemUid, { progress });
    } catch (error) {
      console.error('진행률 업데이트 실패:', error);
    }
  };

  /**
   * 오류 처리 및 상태 업데이트
   * @param {string} itemUid 항목 ID
   * @param {Error} error 오류 객체
   * @param {string} defaultMsgKey 기본 오류 메시지 키
   */
  const handleError = (itemUid, error, defaultMsgKey) => {
    console.error('다운로드 오류:', error);

    // 항목 상태 업데이트
    updateItemStatus(itemUid, DOWNLOAD_STATUS.ERROR);

    // 사용자에게 오류 메시지 표시
    message.error(getErrorMessage(error, defaultMsgKey));

    // 활성 다운로드에서 제거
    cleanupDownload(itemUid);
  };

  /**
   * 다운로드 정리 및 상태 업데이트
   * @param {string} itemUid 항목 ID
   */
  const cleanupDownload = (itemUid) => {
    // 폴링 타이머 정리
    if (pollingTimers[itemUid]) {
      clearInterval(pollingTimers[itemUid]);
      delete pollingTimers[itemUid];
    }

    // 활성 다운로드에서 제거
    delete activeDownloads[itemUid];

    // 다운로드 상태 업데이트
    isDownloading = Object.keys(activeDownloads).length > 0;

    // 자동 닫기 확인
    setAutoCloseTimer();
  };

  // ========== 다운로드 로직 ==========

  /**
   * 다운로드 시작 함수
   * @param {Array} items 다운로드 항목 배열
   * @param {string} currentDir 현재 디렉토리 경로
   * @param {Object} selectedDrive 선택된 드라이브 정보
   */
  const startDownload = async (items, currentDir, selectedDrive) => {
    if (!items || items.length === 0) return;

    isDownloading = true;

    // 다운로드 요청 데이터 준비
    const requestData = {
      currentDir,
      targets: items.map((item) => ({
        path: item.path,
        type: item.type === 'dir' ? 'dir' : 'file',
      })),
      userId: localStorage.getItem('userId'),
      userName: localStorage.getItem('username'),
      driveName: selectedDrive?.driveName,
    };

    // 다운로드 항목 생성
    const itemUid = generateDownloadId();
    const displayName = createDisplayName(items);
    const totalSize = calculateTotalSize(items);

    const downloadItem = {
      uid: itemUid,
      name: displayName,
      size: totalSize,
      items,
    };

    // UI 상태 업데이트 및 활성 다운로드에 추가
    if (stateUpdater) {
      if (typeof stateUpdater.addItems === 'function') {
        stateUpdater.addItems([downloadItem]);
      }

      // 초기 상태와 메시지 설정 (단일 파일 vs 다중 파일/폴더)
      if (items.length === 1 && items[0].type !== 'dir') {
        updateItemStatus(
          itemUid,
          DOWNLOAD_STATUS.PREPARING,
          localeResource['txt.file-transfer.downloading'],
        );
      } else {
        updateItemStatus(
          itemUid,
          DOWNLOAD_STATUS.PREPARING,
          localeResource['txt.file-transfer.compressing'],
        );
      }

      activeDownloads[itemUid] = downloadItem;
    }

    try {
      // 다운로드 요청 API 호출
      const response = await downloadService.requestDownload(requestData);

      // API 응답 검증 - 파일 다운로드 API 문서에 맞게 수정
      if (!response?.data?.success) {
        throw new Error(
          response?.data?.errorMessage || 'txt.file-transfer.msg-download-request-failed',
        );
      }

      // 응답에서 jobId 추출
      const { jobId } = response.data.data;
      downloadItem.jobId = jobId;

      // jobId를 받았으면 바로 상태 확인 API를 호출해서 현재 상태 확인
      try {
        const statusResponse = await downloadService.checkDownloadStatus(jobId);

        if (statusResponse?.data?.success) {
          const { status, downloadUri } = statusResponse.data.data;

          if (status === DOWNLOAD_STATUS.READY && downloadUri) {
            // 준비 완료 상태면 바로 다운로드
            updateItemStatus(
              itemUid,
              DOWNLOAD_STATUS.READY,
              localeResource['txt.file-transfer.download-ready'],
            );
            downloadFile(itemUid, jobId, downloadUri);
          } else if (status === DOWNLOAD_STATUS.COMPRESSING) {
            // 압축 중이면 상태 업데이트 후 폴링 시작
            updateItemStatus(
              itemUid,
              DOWNLOAD_STATUS.COMPRESSING,
              localeResource['txt.file-transfer.compressing'],
            );
            pollDownloadStatus(itemUid, jobId);
          } else if (status === DOWNLOAD_STATUS.FAILED) {
            throw new Error('txt.file-transfer.msg-download-failed');
          } else {
            // 기본적으로는 압축 중으로 처리하고 폴링 시작
            updateItemStatus(
              itemUid,
              DOWNLOAD_STATUS.COMPRESSING,
              localeResource['txt.file-transfer.compressing'],
            );
            pollDownloadStatus(itemUid, jobId);
          }
        } else {
          // 상태 확인 실패시 압축 중으로 간주하고 폴링 시작
          updateItemStatus(
            itemUid,
            DOWNLOAD_STATUS.COMPRESSING,
            localeResource['txt.file-transfer.compressing'],
          );
          pollDownloadStatus(itemUid, jobId);
        }
      } catch (statusError) {
        console.error('다운로드 상태 확인 오류:', statusError);
        // 상태 확인 오류시 압축 중으로 간주하고 폴링 시작
        updateItemStatus(
          itemUid,
          DOWNLOAD_STATUS.COMPRESSING,
          localeResource['txt.file-transfer.compressing'],
        );
        pollDownloadStatus(itemUid, jobId);
      }
    } catch (error) {
      console.error('다운로드 요청 오류:', error);
      handleError(itemUid, error, 'txt.file-transfer.msg-download-request-failed');
    }
  };

  /**
   * 다운로드 상태 확인 폴링
   * @param {string} itemUid 항목 ID
   * @param {string} jobId 작업 ID
   */
  const pollDownloadStatus = (itemUid, jobId) => {
    pollingTimers[itemUid] = setInterval(async () => {
      try {
        const response = await downloadService.checkDownloadStatus(jobId);

        // API 응답 검증
        if (!response?.data?.success) {
          console.error('다운로드 상태 확인 실패:', response);
          throw new Error('txt.file-transfer.msg-download-check-failed');
        }

        const { status } = response.data.data;
        switch (status) {
          case DOWNLOAD_STATUS.READY: {
            // 준비 완료 - 폴링 중지 및 다운로드 시작
            clearInterval(pollingTimers[itemUid]);
            delete pollingTimers[itemUid];

            // 준비 완료 메시지로 상태 업데이트
            updateItemStatus(
              itemUid,
              DOWNLOAD_STATUS.READY,
              localeResource['txt.file-transfer.download-ready'],
            );

            const formattedUri = downloadService.getDownloadUrl(jobId);
            downloadFile(itemUid, jobId, formattedUri);
            break;
          }

          case DOWNLOAD_STATUS.COMPRESSING:
            // 계속 압축 중 - 메시지 업데이트
            updateItemStatus(
              itemUid,
              DOWNLOAD_STATUS.COMPRESSING,
              localeResource['txt.file-transfer.compressing'],
            );
            break;

          case DOWNLOAD_STATUS.FAILED:
            // 실패 - 폴링 중지 및 오류 처리
            console.error('다운로드 실패');
            clearInterval(pollingTimers[itemUid]);
            delete pollingTimers[itemUid];

            updateItemStatus(itemUid, DOWNLOAD_STATUS.ERROR);
            if (localeResource && localeResource['txt.file-transfer.msg-download-failed']) {
              message.error(localeResource['txt.file-transfer.msg-download-failed']);
            } else {
              message.error('다운로드에 실패했습니다.');
            }

            cleanupDownload(itemUid);
            break;

          default:
            // 알 수 없는 상태
            console.warn(`알 수 없는 다운로드 상태: ${status}`);
            updateItemStatus(
              itemUid,
              DOWNLOAD_STATUS.COMPRESSING,
              `상태 확인 중... (${status || '알 수 없음'})`,
            );
        }
      } catch (error) {
        // 폴링 중 오류 - 폴링 중지 및 오류 처리
        console.error('다운로드 폴링 오류:', error);
        clearInterval(pollingTimers[itemUid]);
        delete pollingTimers[itemUid];

        handleError(itemUid, error, 'txt.file-transfer.msg-download-check-failed');
      }
    }, 1000); // 1초 간격으로 폴링
  };

  /**
   * 실제 파일 다운로드 처리
   * @param {string} itemUid 항목 ID
   * @param {string} jobId 작업 ID
   * @param {string} downloadUri 다운로드 URI (선택적)
   */
  const downloadFile = (itemUid, jobId, downloadUri) => {
    try {
      const downloadItem = activeDownloads[itemUid];
      if (!downloadItem) return;

      // 상태 업데이트
      updateItemStatus(itemUid, DOWNLOAD_STATUS.DOWNLOADING);

      // 진행률 업데이트는 stateUpdater.updateProgress가 존재할 때만 시도
      if (stateUpdater && typeof stateUpdater.updateProgress === 'function') {
        updateProgress(itemUid, 100);
      }
      const downloadUrl = `${window.location.origin}${downloadUri}`;

      // 다운로드 링크 생성 및 클릭 (브라우저 다운로드 시작)
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.setAttribute('download', downloadItem.name);
      link.setAttribute('target', '_blank');
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // 완료 상태로 업데이트
      updateItemStatus(itemUid, DOWNLOAD_STATUS.COMPLETED);

      // 활성 다운로드 목록에서 제거 (지연)
      setTimeout(() => {
        cleanupDownload(itemUid);
      }, 1000); // 1초 후 제거 (UI 표시를 위해)
    } catch (error) {
      handleError(itemUid, error, 'txt.file-transfer.msg-download-failed');
    }
  };

  /**
   * 다운로드 취소
   * @param {string} itemUid 항목 ID
   * @returns {boolean} 취소 성공 여부
   */
  const cancelDownload = (itemUid) => {
    // 폴링 중지
    if (pollingTimers[itemUid]) {
      clearInterval(pollingTimers[itemUid]);
      delete pollingTimers[itemUid];
    }

    // 상태 업데이트
    updateItemStatus(itemUid, DOWNLOAD_STATUS.CANCELLED);

    // 활성 다운로드에서 제거
    cleanupDownload(itemUid);

    return true;
  };

  /**
   * 모든 다운로드 취소
   * @returns {boolean} 취소 성공 여부
   */
  const cancelAllDownloads = () => {
    // 모든 폴링 중지
    Object.keys(pollingTimers).forEach((itemUid) => {
      clearInterval(pollingTimers[itemUid]);
      delete pollingTimers[itemUid];
    });

    // 모든 다운로드 항목 취소 처리
    Object.keys(activeDownloads).forEach((itemUid) => {
      updateItemStatus(itemUid, DOWNLOAD_STATUS.CANCELLED);
    });

    // 모든 활성 다운로드 제거
    Object.keys(activeDownloads).forEach((key) => delete activeDownloads[key]);
    isDownloading = false;

    // 자동 닫기 타이머 취소
    if (autoCloseTimer) {
      clearTimeout(autoCloseTimer);
      autoCloseTimer = null;
    }

    // 사용자에게 알림
    message.info(localeResource['txt.file-transfer.msg-all-downloads-cancelled']);

    return true;
  };

  return {
    /**
     * 상태 업데이터 설정
     * @param {Object} updater 상태 업데이트 함수 모음
     */
    setStateUpdater: (updater) => {
      stateUpdater = updater;
    },

    /**
     * 로케일 리소스 설정
     * @param {Object} resource
     */
    setLocaleResource: (resource) => {
      localeResource = resource;
    },

    /**
     * 파일 다운로드 시작
     * @param {Array} files 다운로드할 파일/폴더 목록
     * @param {string} currentDir 현재 디렉토리 경로
     */
    downloadFiles: (files, currentDir, selectedDrive) => {
      startDownload(files, currentDir, selectedDrive);
    },

    /**
     * 특정 다운로드 취소
     * @param {string} itemId 다운로드 항목 ID
     * @returns {boolean} 취소 성공 여부
     */
    cancelDownload,

    /**
     * 모든 다운로드 취소
     * @returns {boolean} 취소 성공 여부
     */
    cancelAllDownloads,

    /**
     * 진행 중인 다운로드가 있는지 확인
     * @returns {boolean} 활성 다운로드 여부
     */
    hasActiveDownloads: () => isDownloading,
  };
}

// 인스턴스 생성 및 내보내기
const downloadManagerInstance = createDownloadManager();
export default downloadManagerInstance;
